/**
 * SPA route change detector. Cluster D items 31, 33, 35, 36.
 *
 * Detection layers (in order of reliability):
 *   1. history.pushState / replaceState interception — covers React Router,
 *      Vue Router, Next.js, Angular, SvelteKit, almost every modern SPA.
 *   2. popstate — back/forward + programmatic back.
 *   3. hashchange — for hash routers (`/#/page` style). Item 33.
 *   4. MutationObserver scoped to the main content area (not <title>).
 *      Catches SPAs that update the DOM without touching history (rare,
 *      but tour-heavy products do this). Item 31.
 *
 * On detection, we also snapshot:
 *   - Skeleton-state heuristic — defer "ready" until the route's content
 *     actually paints (not the loading shimmer). Item 35.
 *   - Soft-error detection — flag "404 / Error / Something went wrong"
 *     pages so the agent doesn't proceed onto them. Item 36.
 */

export type RouteChangeCallback = (info: {
  url: string;
  title: string;
  /** True when the new view appears to be a skeleton / loading placeholder. */
  isLoading: boolean;
  /** Soft error text spotted on the page (404, "something went wrong", …). */
  softError?: string;
}) => void;

const SOFT_ERROR_RE = /\b(404|not found|page not found|something went wrong|access denied|unauthorized|server error|503|502)\b/i;
const SKELETON_CLASS_RE = /(skeleton|shimmer|placeholder|loading|spinner)/i;

export class SPADetector {
  private lastUrl = '';
  private lastTitle = '';
  private observer: MutationObserver | null = null;
  private callback: RouteChangeCallback;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingCheck = false;

  constructor(callback: RouteChangeCallback) {
    this.callback = callback;
    this.lastUrl = location.href;
    this.lastTitle = document.title;
  }

  start(): void {
    this.interceptHistoryAPI();
    window.addEventListener('popstate', this.handleChange);
    // Item 33: hash routers.
    window.addEventListener('hashchange', this.handleChange);

    // Item 31: scope mutation observation to the actual content surface,
    // not the document <head>. Title-only observation missed any SPA that
    // didn't update <title> on route change (most modern apps don't).
    const target =
      document.querySelector('main') ??
      document.querySelector('[role="main"]') ??
      document.querySelector('#root') ??
      document.querySelector('#app') ??
      document.body;
    this.observer = new MutationObserver(() => this.throttledCheck());
    this.observer.observe(target ?? document.documentElement, {
      childList: true,
      subtree: true,
      characterData: false,
      attributes: false,
    });
  }

  stop(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }
    window.removeEventListener('popstate', this.handleChange);
    window.removeEventListener('hashchange', this.handleChange);
  }

  private handleChange = (): void => {
    // Small delay for DOM to settle after route change. Re-check skeleton
    // state inside checkForChange so a page that lands as a skeleton and
    // resolves quickly fires once with isLoading=false.
    setTimeout(() => this.checkForChange(), 100);
  };

  /** Throttle MutationObserver callbacks to max once per 500ms */
  private throttledCheck(): void {
    if (this.throttleTimer) {
      this.pendingCheck = true;
      return;
    }
    this.checkForChange();
    this.throttleTimer = setTimeout(() => {
      this.throttleTimer = null;
      if (this.pendingCheck) {
        this.pendingCheck = false;
        this.checkForChange();
      }
    }, 500);
  }

  private checkForChange(): void {
    const currentUrl = location.href;
    const currentTitle = document.title;
    if (currentUrl === this.lastUrl && currentTitle === this.lastTitle) return;
    this.lastUrl = currentUrl;
    this.lastTitle = currentTitle;

    this.callback({
      url: currentUrl,
      title: currentTitle,
      isLoading: detectSkeletonState(),
      softError: detectSoftError(),
    });
  }

  private interceptHistoryAPI(): void {
    const origPushState = history.pushState.bind(history);
    const origReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      origPushState(...args);
      this.handleChange();
    };

    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      origReplaceState(...args);
      this.handleChange();
    };
  }
}

/**
 * Item 35 — skeleton heuristic. Returns true when the visible content area
 * is dominated by skeleton/shimmer placeholders. Cheap to compute (single
 * tree walk over the main region's first ~200 elements).
 */
export function detectSkeletonState(): boolean {
  const root =
    document.querySelector('main') ??
    document.querySelector('[role="main"]') ??
    document.querySelector('#root') ??
    document.querySelector('#app') ??
    document.body;
  if (!root) return false;
  const sample = Array.from(root.querySelectorAll('*')).slice(0, 200);
  if (sample.length === 0) return false;
  let skeletonHits = 0;
  let textfulNodes = 0;
  for (const el of sample) {
    const className = el.getAttribute('class') ?? '';
    if (SKELETON_CLASS_RE.test(className)) skeletonHits++;
    const text = (el as HTMLElement).innerText?.trim();
    if (text && text.length > 8) textfulNodes++;
  }
  // > 5% skeleton elements OR very low textful-node count → loading.
  return skeletonHits >= 3 || textfulNodes < 4;
}

/**
 * Item 36 — soft-error detection. Returns the matched error phrase when the
 * page renders a 404 / unauthorized / "something went wrong" state, or
 * undefined otherwise. The agent uses this to surface to the visitor and
 * pivot, rather than narrating into a broken view.
 */
export function detectSoftError(): string | undefined {
  // Check h1/h2 first — error pages typically lead with the message.
  const headings = Array.from(document.querySelectorAll('h1, h2'));
  for (const h of headings) {
    const txt = (h as HTMLElement).innerText?.trim() ?? '';
    const match = SOFT_ERROR_RE.exec(txt);
    if (match) return match[0];
  }
  // Fallback: first 1KB of main content.
  const main =
    document.querySelector('main') ??
    document.querySelector('[role="main"]') ??
    document.body;
  if (!main) return undefined;
  const text = ((main as HTMLElement).innerText ?? '').slice(0, 1024);
  const m = SOFT_ERROR_RE.exec(text);
  return m ? m[0] : undefined;
}
